import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Upload, Plus, Globe, DollarSign, User, Music, Link as LinkIcon, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockArtists, mockEvents } from '@/lib/mockData';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';

// Payment method icons for visual clarity
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.65a.766.766 0 0 1 .756-.65h6.09c3.018 0 5.236 1.65 4.824 4.848-.543 4.254-3.673 5.88-6.887 5.88H7.672l-.596 8.609zm11.578-17.34c.588 4.37-1.686 6.904-5.11 7.512.063-.62.112-1.25.138-1.89.35-2.877-.78-4.37-3.26-4.37H8.91l1.09-1.252h4.654c1.766 0 3.09.592 4 2z"/>
  </svg>
);

const VenmoIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M19.5 2.25C20.4 3.9 20.8 5.6 20.8 7.65C20.8 12.85 16.35 19.5 12.8 23.25H4.2L1.2 3.5L8.85 2.8L10.45 16.05C11.95 13.5 13.8 9.5 13.8 6.5C13.8 4.55 13.45 3.2 12.85 2.1L19.5 2.25Z"/>
  </svg>
);

const CashAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h2.39c.14.89.77 1.5 1.77 1.5 1.11 0 1.67-.56 1.67-1.23 0-.79-.47-1.13-1.92-1.55-2.31-.66-3.54-1.63-3.54-3.48 0-1.63 1.25-2.86 3.02-3.19V5h2.67v1.75c1.58.36 2.72 1.39 2.85 3.1h-2.38c-.11-.74-.59-1.28-1.47-1.28-.82 0-1.42.45-1.42 1.14 0 .63.39 1.02 1.85 1.43 2.41.66 3.63 1.62 3.63 3.59 0 1.77-1.36 3.05-3.18 3.36z"/>
  </svg>
);

export function AdminAddArtist() {
  const navigate = useNavigate();
  const { artistId } = useParams();
  const isEditMode = Boolean(artistId);
  
  const [formData, setFormData] = useState({
    name: '',
    instrument: '',
    website: '',
    bio: '',
    image: null,
    tipLinks: {
      paypal: '',
      venmo: '',
      cashApp: '',
      customTip: ''
    }
  });

  // Find artist events for edit mode
  const artistEvents = isEditMode 
    ? mockEvents.filter(e => e.artists?.some(a => a.id === artistId))
    : [];

  // Load artist data in edit mode
  useEffect(() => {
    if (isEditMode && artistId) {
      const artist = mockArtists.find(a => a.id === artistId);
      if (artist) {
        setFormData({
          name: artist.name || '',
          instrument: artist.instrument || '',
          website: artist.website || '',
          bio: artist.bio || '',
          image: artist.image || null,
          tipLinks: {
            paypal: artist.tipLinks?.paypal || '',
            venmo: artist.tipLinks?.venmo || '',
            cashApp: artist.tipLinks?.cashApp || '',
            customTip: artist.tipLinks?.customTip || ''
          }
        });
      }
    }
  }, [artistId, isEditMode]);

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Artist name is required');
      return;
    }
    
    if (isEditMode) {
      // Mock update
      toast.success('Artist updated successfully!');
    } else {
      // Mock save
      toast.success('Artist added successfully!');
    }
    navigate('/admin/artists');
  };

  const updateTipLink = (field, value) => {
    setFormData({
      ...formData,
      tipLinks: {
        ...formData.tipLinks,
        [field]: value
      }
    });
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-2xl mx-auto">
        <Link to="/admin/artists" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Artists
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2" data-testid="page-title">
          {isEditMode ? 'Edit Artist' : '+ Add New Artist'}
        </h1>
        <p className="text-muted-foreground mb-8">
          {isEditMode ? 'Update artist information' : 'Add a new artist to your roster'}
        </p>

        <Card className="bg-card border-border p-6">
          <div className="space-y-6">
            {/* Artist Image */}
            <div className="flex items-start gap-4">
              <div className="w-32 h-32 rounded-xl bg-secondary border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors overflow-hidden">
                {formData.image ? (
                  <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <Plus className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-xs text-muted-foreground">Add Photo</span>
                  </>
                )}
              </div>
              <div className="flex-1">
                <Button variant="outline" className="border-border">
                  <Upload className="w-4 h-4 mr-2" />
                  {isEditMode ? 'CHANGE' : 'ADD'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">PNG, JPG up to 5MB</p>
              </div>
            </div>

            {/* Artist Name */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Artist name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Enter artist name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-secondary border-border pl-10"
                  data-testid="artist-name-input"
                />
              </div>
            </div>

            {/* Instrument / Speciality */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Instrument / speciality</label>
              <div className="relative">
                <Music className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="e.g., Guitar, Vocals, DJ"
                  value={formData.instrument}
                  onChange={(e) => setFormData({...formData, instrument: e.target.value})}
                  className="bg-secondary border-border pl-10"
                  data-testid="artist-instrument-input"
                />
              </div>
            </div>

            {/* Website Link */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Website link</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  type="url"
                  placeholder="https://artist-website.com"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  className="bg-secondary border-border pl-10"
                  data-testid="artist-website-input"
                />
              </div>
            </div>

            {/* Tipping Links Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-white">Tipping Links</h3>
              </div>
              <p className="text-xs text-muted-foreground -mt-2 mb-4">
                Add payment links for fans to tip this artist. Leave empty to hide that option.
              </p>

              {/* PayPal URL */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <span className="text-[#003087]"><PayPalIcon /></span>
                  PayPal URL
                </label>
                <Input 
                  type="url"
                  placeholder="https://paypal.me/artistname"
                  value={formData.tipLinks.paypal}
                  onChange={(e) => updateTipLink('paypal', e.target.value)}
                  className="bg-secondary border-border"
                  data-testid="paypal-input"
                />
              </div>

              {/* Venmo URL */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <span className="text-[#3D95CE]"><VenmoIcon /></span>
                  Venmo URL
                </label>
                <Input 
                  type="url"
                  placeholder="https://venmo.com/u/artistname"
                  value={formData.tipLinks.venmo}
                  onChange={(e) => updateTipLink('venmo', e.target.value)}
                  className="bg-secondary border-border"
                  data-testid="venmo-input"
                />
              </div>

              {/* Cash App URL */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <span className="text-[#00D632]"><CashAppIcon /></span>
                  Cash App URL
                </label>
                <Input 
                  type="url"
                  placeholder="https://cash.app/$artistname"
                  value={formData.tipLinks.cashApp}
                  onChange={(e) => updateTipLink('cashApp', e.target.value)}
                  className="bg-secondary border-border"
                  data-testid="cashapp-input"
                />
              </div>

              {/* Custom Tip URL */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-purple-500" />
                  Custom Tip URL (Ko-fi, Patreon, etc.)
                </label>
                <Input 
                  type="url"
                  placeholder="https://ko-fi.com/artistname"
                  value={formData.tipLinks.customTip}
                  onChange={(e) => updateTipLink('customTip', e.target.value)}
                  className="bg-secondary border-border"
                  data-testid="custom-tip-input"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Bio</label>
              <textarea
                placeholder="Tell us about this artist..."
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                className="w-full bg-secondary border border-border rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none h-32"
                data-testid="artist-bio-input"
              />
            </div>

            {/* Submit Button */}
            <Button 
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12"
              onClick={handleSubmit}
              data-testid="submit-artist-btn"
            >
              {isEditMode ? 'SAVE CHANGES' : 'ADD ARTIST'}
            </Button>
          </div>
        </Card>

        {/* Artist Events Section - Only shown in Edit Mode */}
        {isEditMode && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Artist Events
            </h2>
            
            {artistEvents.length > 0 ? (
              <div className="space-y-3">
                {artistEvents.map(event => (
                  <Link key={event.id} to={`/admin/events/${event.id}`}>
                    <Card className="bg-card border-border p-4 hover:border-primary/50 transition-colors mb-3">
                      <div className="flex items-center gap-4">
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-white">{event.title}</h3>
                          <p className="text-sm text-muted-foreground">{event.venue}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(event.date)}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          event.status === 'upcoming' 
                            ? 'bg-primary/20 text-primary' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {event.status === 'upcoming' ? 'Upcoming' : 'Past'}
                        </span>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="bg-card border-border p-6 text-center">
                <p className="text-muted-foreground">No events found for this artist.</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminAddArtist;
